/**
 * Client cardio logging — now prescription-aware.
 *
 * Shows the client's prescribed weekly cardio (set by their trainer). Tapping a
 * prescribed item pre-fills the activity + target. The client can substitute a
 * different activity (logs what they actually did, keeps the prescription as the
 * plan of record via prescription_id), or log ad-hoc cardio not on their plan.
 * Activities come from the real cardio_machines list (Treadmill, PX90, etc.).
 *
 * POSTs to /api/workouts/logs with workout_type:'cardio' + a cardio_block.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Check, Timer, Bike, CalendarCheck } from 'lucide-react';
import { api } from '@/lib/api';

interface Machine { id: string; name: string; type: string | null; }
interface Prescription {
  id: string;
  machine_id: string;
  activity_name: string | null;
  target_minutes: number | null;
  day_of_week: number | null;
  frequency_per_week: number | null;
  date?: string | null;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function CardioLog() {
  const navigate = useNavigate();
  const [machines, setMachines] = useState<Machine[]>([]);
  const [plan, setPlan] = useState<Prescription[]>([]);
  const [machineId, setMachineId] = useState<string>('');
  const [prescriptionId, setPrescriptionId] = useState<string | null>(null);
  const [minutes, setMinutes] = useState('');
  const [distance, setDistance] = useState('');
  const [calories, setCalories] = useState('');
  const [heartRate, setHeartRate] = useState('');
  const [rpe, setRpe] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const todayDow = new Date().getDay();

  useEffect(() => {
    Promise.all([
      api.get<{ machines: Machine[] }>('/api/workouts/machines'),
      api.get<{ prescriptions: Prescription[] }>('/api/workouts/cardio-plan'),
    ]).then(([m, p]) => {
      setMachines(m.machines || []);
      setPlan(p.prescriptions || []);
      if ((m.machines || []).length) setMachineId(m.machines[0].id);
    }).catch(() => {});
  }, []);

  // Prescribed items relevant today (today's weekly + one-time for today, and
  // all frequency-based).
  const todayIso = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  const todaysPlan = plan.filter((p) => (p.date ? p.date === todayIso : p.day_of_week === todayDow));
  const freqPlan = plan.filter((p) => p.day_of_week == null && !p.date);

  function pickPrescribed(p: Prescription) {
    setMachineId(p.machine_id);
    setPrescriptionId(p.id);
    if (p.target_minutes) setMinutes(String(p.target_minutes));
  }

  const selectedName = machines.find((m) => m.id === machineId)?.name ?? 'Cardio';

  async function handleSave() {
    if (!machineId) return;
    setSaving(true);
    try {
      await api.post('/api/workouts/logs', {
        workout_type: 'cardio',
        duration_minutes: minutes ? parseInt(minutes) : null,
        notes: selectedName,
        cardio_blocks: [
          {
            machine_id: machineId,
            prescription_id: prescriptionId,
            actual_minutes: minutes ? parseFloat(minutes) : null,
            actual_distance: distance ? parseFloat(distance) : null,
            actual_calories: calories ? parseFloat(calories) : null,
            actual_avg_heart_rate: heartRate ? parseFloat(heartRate) : null,
            rpe: rpe ? parseInt(rpe) : null,
          },
        ],
      });
      setSaved(true);
    } catch {
      /* keep the form for retry */
    } finally {
      setSaving(false);
    }
  }

  if (saved) {
    return (
      <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Check size={64} style={{ color: 'var(--success)' }} />
        <h1 className="display mt-4" style={{ fontSize: '2.1rem' }}>Cardio Saved</h1>
        <button onClick={() => navigate('/workouts')} className="btn-gold w-full mt-6">Done</button>
      </div>
    );
  }

  return (
    <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] pb-4">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-1" style={{ color: 'var(--champagne)' }} aria-label="Back">
          <ArrowLeft size={22} />
        </button>
        <h1 className="display" style={{ fontSize: '1.9rem' }}>Log Cardio</h1>
      </div>

      <div className="flex flex-col gap-4">
        {/* Prescribed cardio plan */}
        {(todaysPlan.length > 0 || freqPlan.length > 0) && (
          <div className="card">
            <div className="flex items-center gap-2 mb-3">
              <CalendarCheck size={16} style={{ color: 'var(--champagne)' }} />
              <p className="text-sm font-semibold" style={{ color: 'var(--cream)' }}>Your Plan</p>
            </div>
            {todaysPlan.length > 0 && (
              <>
                <p className="label">Today ({DAYS[todayDow]})</p>
                <div className="flex flex-col gap-2 mb-3">
                  {todaysPlan.map((p) => (
                    <button key={p.id} onClick={() => pickPrescribed(p)}
                      className="flex items-center justify-between text-left px-3 py-2"
                      style={{ borderRadius: 'var(--radius-md)', background: prescriptionId === p.id ? 'rgba(201,168,106,0.15)' : 'var(--surface)', border: prescriptionId === p.id ? '1px solid var(--champagne-muted)' : '1px solid transparent' }}>
                      <span className="text-sm" style={{ color: 'var(--cream)' }}>{p.activity_name}</span>
                      <span className="text-xs" style={{ color: 'var(--cream-muted)' }}>{p.target_minutes ? `${p.target_minutes} min` : ''}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
            {freqPlan.length > 0 && (
              <>
                <p className="label">Weekly</p>
                <div className="flex flex-col gap-2">
                  {freqPlan.map((p) => (
                    <button key={p.id} onClick={() => pickPrescribed(p)}
                      className="flex items-center justify-between text-left px-3 py-2"
                      style={{ borderRadius: 'var(--radius-md)', background: prescriptionId === p.id ? 'rgba(201,168,106,0.15)' : 'var(--surface)', border: prescriptionId === p.id ? '1px solid var(--champagne-muted)' : '1px solid transparent' }}>
                      <span className="text-sm" style={{ color: 'var(--cream)' }}>{p.activity_name}</span>
                      <span className="text-xs" style={{ color: 'var(--cream-muted)' }}>{p.frequency_per_week}x/wk{p.target_minutes ? ` · ${p.target_minutes} min` : ''}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
            {prescriptionId && (
              <p className="text-xs mt-3" style={{ color: 'var(--cream-muted)' }}>
                Logging against your plan. You can swap the activity below to substitute.
              </p>
            )}
          </div>
        )}

        {/* Activity selector (real list — substitution-friendly) */}
        <div>
          <label className="label">Activity</label>
          <div className="flex flex-wrap gap-2">
            {machines.map((m) => (
              <button key={m.id} onClick={() => setMachineId(m.id)}
                className="px-3 py-1.5 text-xs font-medium transition-all"
                style={{ borderRadius: 'var(--radius-sm)', background: machineId === m.id ? 'var(--champagne)' : 'var(--surface)', color: machineId === m.id ? 'var(--ink)' : 'var(--cream-muted)' }}>
                {m.name}
              </button>
            ))}
          </div>
        </div>

        {/* Metrics */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Bike size={16} style={{ color: 'var(--champagne)' }} />
            <p className="text-sm font-semibold" style={{ color: 'var(--cream)' }}>{selectedName}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label flex items-center gap-1"><Timer size={10} /> Duration (min)</label>
              <input type="number" inputMode="numeric" className="input" placeholder="0" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
            </div>
            <div>
              <label className="label">Distance (mi)</label>
              <input type="number" inputMode="decimal" className="input" placeholder="0" value={distance} onChange={(e) => setDistance(e.target.value)} />
            </div>
            <div>
              <label className="label">Calories</label>
              <input type="number" inputMode="numeric" className="input" placeholder="0" value={calories} onChange={(e) => setCalories(e.target.value)} />
            </div>
            <div>
              <label className="label">Avg Heart Rate</label>
              <input type="number" inputMode="numeric" className="input" placeholder="0" value={heartRate} onChange={(e) => setHeartRate(e.target.value)} />
            </div>
          </div>
          <div className="mt-3">
            <label className="label">RPE (1-10)</label>
            <div className="flex gap-1">
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <button key={n} onClick={() => setRpe(String(n))}
                  className="flex-1 py-2 text-xs font-medium transition-all"
                  style={{ borderRadius: 'var(--radius-sm)', background: rpe === String(n) ? 'var(--champagne)' : 'var(--surface)', color: rpe === String(n) ? 'var(--ink)' : 'var(--cream-muted)' }}>
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>

        <button onClick={handleSave} className="btn-gold w-full" disabled={saving || !machineId}>
          {saving ? 'Saving…' : 'Save Cardio'}
        </button>
      </div>
    </div>
  );
}
